import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, getLandingPath } from '../lib/auth';
import { authAPI } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Check, X, Loader2, ShieldCheck, CheckCircle2, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

/**
 * Forced password reset (first login) + voluntary password change from profile.
 * - Reads route state `firstLogin` to display the welcome-style banner.
 * - Rejects submit until all 5 rules pass + new == confirm.
 */
const RULES = [
  { key: 'len', label: 'At least 12 characters', test: (s) => s.length >= 12 },
  { key: 'upper', label: 'Includes an uppercase letter', test: (s) => /[A-Z]/.test(s) },
  { key: 'lower', label: 'Includes a lowercase letter', test: (s) => /[a-z]/.test(s) },
  { key: 'digit', label: 'Includes a digit', test: (s) => /\d/.test(s) },
  { key: 'special', label: 'Includes a special character (!@#$%…)', test: (s) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~`]/.test(s) },
];

const Rule = ({ ok, label }) => (
  <div className={`flex items-center gap-2 text-xs ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
    {ok ? <Check size={14} /> : <X size={14} />}
    <span>{label}</span>
  </div>
);

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser } = useAuth();
  const firstLogin = location.state?.firstLogin || user?.must_change_password;

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);  // v36: success screen flag
  // Per-hospital password policy toggle — when OFF, hide PCI rules and only require 4+ chars.
  const [policyEnforced, setPolicyEnforced] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/security/password-policy');
        setPolicyEnforced(res.data?.password_policy_enforced !== false);
      } catch { /* default to enforced */ }
    })();
  }, []);

  const ruleResults = useMemo(() => RULES.map(r => ({ ...r, ok: r.test(newPw) })), [newPw]);
  const allRulesPass = policyEnforced ? ruleResults.every(r => r.ok) : (newPw && newPw.length >= 4);
  const matches = newPw && newPw === confirmPw;
  const canSubmit = currentPw && allRulesPass && matches && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await authAPI.changePassword(currentPw, newPw);
      // Update cached localStorage so any reads downstream see the fresh flag
      const cached = JSON.parse(localStorage.getItem('user') || '{}');
      cached.must_change_password = false;
      localStorage.setItem('user', JSON.stringify(cached));
      // CRITICAL (v36 — fixes login/reset loop): Refresh the auth context's user state so
      // DashboardLayout's `if (user.must_change_password)` redirect releases. Without this,
      // the stale React state kept bouncing the user back to /change-password.
      try { await refreshUser(); } catch { /* non-blocking */ }
      toast.success('Password updated successfully.');
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      let message = detail || 'Could not update password. Try again.';
      // Make the "current password is incorrect" path actionable for first-time users.
      if (firstLogin && /current password is incorrect/i.test(detail)) {
        message = "The temporary password doesn't match. Please copy it again from your Welcome Email — or ask your admin to reset access.";
      }
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // v36 — Success screen: shown once password change succeeds. Replaces the silent re-render
  // that previously made users feel they were stuck in a loop.
  if (success) {
    const cached = JSON.parse(localStorage.getItem('user') || '{}');
    const landing = getLandingPath(cached);
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-6" data-testid="change-password-success">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-emerald-200 p-10 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
            <CheckCircle2 className="text-emerald-600" size={36} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Password Updated</h1>
          <p className="text-sm text-slate-600 mb-7">
            Your password has been successfully changed. You're all set — let's get you into the app.
          </p>
          <Button
            onClick={() => navigate(landing, { replace: true })}
            className="w-full h-11 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold"
            data-testid="success-continue-btn">
            Continue to App <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <button
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
            className="mt-4 text-xs text-slate-500 hover:text-slate-700 hover:underline transition"
            data-testid="success-relogin-btn">
            Or sign in again with the new password
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6" data-testid="change-password-page">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-teal-50 flex items-center justify-center">
            <ShieldCheck className="text-teal-600" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {firstLogin ? 'Set Your New Password' : 'Change Password'}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {firstLogin
                ? 'For your security, please replace the temporary password we shared.'
                : 'Pick a fresh, strong password to keep your account secure.'}
            </p>
          </div>
        </div>

        {firstLogin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-5" data-testid="first-login-banner">
            <strong>First-time login.</strong> Your account is locked until you set a new password.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">{firstLogin ? 'Temporary Password' : 'Current Password'}</Label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type={showCur ? 'text' : 'password'}
                placeholder={firstLogin ? 'From your Welcome Email (e.g., Welcome@123)' : 'Your current password'}
                value={currentPw}
                onChange={e => setCurrentPw(e.target.value)}
                className="pl-11 pr-11 h-11 rounded-lg"
                // Disable browser autofill on first-login so a previously saved password
                // doesn't silently overwrite the temporary password the user is typing.
                autoComplete={firstLogin ? 'off' : 'current-password'}
                name={firstLogin ? `tmp-pw-${Math.random().toString(36).slice(2, 8)}` : 'current-password'}
                data-testid="current-password-input"
                required
              />
              <button type="button" onClick={() => setShowCur(!showCur)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showCur ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type={showNew ? 'text' : 'password'}
                placeholder="Create a new password"
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                className="pl-11 pr-11 h-11 rounded-lg"
                autoComplete="new-password"
                data-testid="new-password-input"
                required
              />
              <button type="button" onClick={() => setShowNew(!showNew)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Live strength rules — hidden when policy is disabled for this hospital */}
            {policyEnforced ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 grid grid-cols-1 gap-1.5" data-testid="password-rules">
                {ruleResults.map(r => (
                  <Rule key={r.key} ok={r.ok} label={r.label} />
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-slate-500 pl-1" data-testid="policy-disabled-hint">
                Your hospital allows any password (minimum 4 characters).
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Confirm New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type={showNew ? 'text' : 'password'}
                placeholder="Re-enter the new password"
                value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)}
                className="pl-11 h-11 rounded-lg"
                autoComplete="new-password"
                data-testid="confirm-password-input"
                required
              />
            </div>
            {confirmPw && !matches && (
              <p className="text-xs text-red-500 pl-1" data-testid="mismatch-warning">Passwords don&apos;t match yet.</p>
            )}
            {matches && (
              <p className="text-xs text-emerald-600 pl-1">Passwords match.</p>
            )}
          </div>

          <Button type="submit" disabled={!canSubmit}
            className="w-full h-11 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-change-password-btn">
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating…</> : (firstLogin ? 'Set Password & Continue' : 'Update Password')}
          </Button>
        </form>

        {firstLogin && (
          <p className="text-center mt-4 text-[11px]">
            <button onClick={() => { logout(); navigate('/login'); }}
              className="text-slate-400 hover:text-red-500 hover:underline transition" data-testid="logout-from-reset-btn">
              Cancel and sign out
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
